import { useContext, useEffect } from "react";
import { isEmpty } from "lodash";
import {
  MdOutlineCurrencyExchange,
  MdOutlineCheck,
  MdOutlineClose,
} from "react-icons/md";
import { GrTransaction } from "react-icons/gr";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import PersianNumber from "../../../utils/PersianNumber/PersianNumber";
import { formatToPersianClock, formatToPersianDate } from "../../../utils";
import { transactionAdminContext } from "../../../context/AdminContext/TransactionAdminContext/TransactionAdminContext";

const AdminLiveTransactions = () => {
  const {
    setCounter,
    getLiveOrders,
    data,
    handleGetLiveOrders,
    handleChangeOrderType,
    handleGetAutoPilot,
    isAutoPilot,
    setIsAutoPilot,
  } = useContext(transactionAdminContext);

  const handleInitial = () => {
    setCounter((prevCounter) => prevCounter + 1);
    handleGetLiveOrders();
  };

  useEffect(() => {
    handleGetAutoPilot();
  }, []);

  useEffect(() => {
    handleInitial();
    const interval = setInterval(handleInitial, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <SiteTitle title="معاملات جاری" />
      <div
        className="2xl:w-[85%] xl:w-[75%] lg:w-[70%] w-full
          left-0 top-0 absolute mx-auto overflow-y-scroll pb-24
          grid gap-8 p-8 justify-items-center h-auto
          2xl:grid-cols-4 xl:grid-cols-3 sm:grid-cols-2 grid-cols-1 
          animate__animated animate__fadeIn animate__slow"
      >
        {isEmpty(getLiveOrders) ? (
          <div className="col-span-full w-full h-full min-h-[60vh] flex items-center justify-center">
            <span
              className="text-gray-300 text-xl space-y-5
              flex flex-col justify-center items-center"
            >
              <span>
                <GrTransaction size={60} />
              </span>
              <p className="text-center">سفارشی وجود ندارد</p>
            </span>
          </div>
        ) : (
          <>
            {getLiveOrders.map((_item) => (
              <div
                key={_item.id}
                className="w-full max-w-[320px] sm:max-w-full min-w-[260px] 
                  h-auto rounded-xl border-b-2 border-[#111827]
                  bg-white/5 backdrop-blur-sm shadow-lg hover:shadow-xl
                  transition-all duration-300 ease-in-out transform hover:-translate-y-1
                  relative p-3 sm:p-4 mx-2 my-2"
              >
                {/* Status Indicator */}
                <div className="absolute top-2 right-2 z-10">
                  {_item.tType === "E" ? (
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-500 opacity-100" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500">
                        <span className="absolute top-5 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-slate-600 bg-white/80 px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                          در حال انتظار
                        </span>
                      </span>
                    </span>
                  ) : _item.tType === "T" ? (
                    <span className="relative flex h-6 w-6">
                      <span className="absolute -top-1 -left-1">
                        <MdOutlineCheck size={20} fill="#22c55e" />
                      </span>
                      <span className="absolute top-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-slate-600 bg-white/80 px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                        تأیید شده
                      </span>
                    </span>
                  ) : _item.tType === "L" ? (
                    <span className="relative flex h-6 w-6">
                      <span className="absolute -top-1 -left-1">
                        <MdOutlineClose size={20} fill="#ef4444" />
                      </span>
                      <span className="absolute top-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-gray-300 bg-white/80 px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                        لغو شده
                      </span>
                    </span>
                  ) : null}
                </div>

                {/* Orderi Icon */}
                {_item.is_orderi && (
                  <div className="absolute top-2 left-2 z-10">
                    <span className="relative flex">
                      <MdOutlineCurrencyExchange
                        size={18}
                        className="text-gray-300"
                      />
                      <span className="absolute top-5 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-gray-300 bg-gray-800/80 px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                        اردری
                      </span>
                    </span>
                  </div>
                )}

                {/* Content */}
                <div className="flex flex-col items-center space-y-2 mt-4">
                  {/* Title */}
                  <div className="text-center w-full px-2">
                    <h3
                      className={`${
                        _item.bos === "B" ? "text-green-600" : "text-red-600"
                      } font-medium text-base sm:text-lg truncate max-w-full`}
                      dir="rtl"
                    >
                      معامله {_item.bos === "B" ? "خرید" : "فروش"} {_item.name}
                    </h3>
                    <p className="text-xs text-gray-300 mt-1">
                      #{_item.transaction_id}
                    </p>
                  </div>

                  {/* Info Grid */}
                  <div className="w-full space-y-3 text-sm">
                    {/* User Name */}
                    <div className="flex flex-wrap items-center justify-center gap-1 px-2">
                      <span className="font-medium text-gray-300 whitespace-nowrap">
                        کاربر :
                      </span>
                      <span
                        className="text-gray-300 truncate max-w-[150px] sm:max-w-[200px]"
                        dir="ltr"
                      >
                        {_item.customeruser}
                      </span>
                    </div>

                    {/* Product Name */}
                    <div className="flex flex-wrap items-center justify-center gap-1 px-2">
                      <span className="font-medium text-gray-300 whitespace-nowrap">
                        نام محصول :
                      </span>
                      <span className="text-gray-300 truncate max-w-[150px] sm:max-w-[200px]">
                        {_item.product_name}
                      </span>
                    </div>

                    {/* Price */}
                    <div className="flex flex-wrap items-center justify-center gap-1 px-2">
                      <span className="font-medium text-gray-300 whitespace-nowrap">
                        {_item.tag === "S" ? "قیمت" : "مظنه"} :
                      </span>
                      <span className="flex items-center gap-1 text-gray-300">
                        <PersianNumber
                          value={_item.price}
                          thousandSeparator=","
                        />
                        <span className="text-xs">تومان</span>
                      </span>
                    </div>

                    {/* Weight/Quantity */}
                    <div className="flex flex-wrap items-center justify-center gap-1 px-2">
                      <span className="font-medium text-gray-300 whitespace-nowrap">
                        {_item.tag === "S" ? "تعداد" : "مقدار (گرم)"} :
                      </span>
                      <span className="text-gray-300">
                        <PersianNumber
                          value={_item.volume}
                          thousandSeparator=","
                        />
                      </span>
                    </div>

                    {/* Total Amount */}
                    <div className="flex flex-wrap items-center justify-center gap-1 px-2">
                      <span className="font-medium text-gray-300 whitespace-nowrap">
                        مبلغ :
                      </span>
                      <span className="flex items-center gap-1 text-gray-300">
                        <PersianNumber
                          value={_item.amount}
                          thousandSeparator=","
                        />
                        <span className="text-xs">تومان</span>
                      </span>
                    </div>

                    {/* Description */}
                    {_item.description && (
                      <div className="flex flex-col items-center gap-1 px-2">
                        <span className="font-medium text-gray-300">
                          توضیحات :
                        </span>
                        <span className="text-gray-300 text-xs text-center break-words max-w-full">
                          {_item.description}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Date & Time */}
                  <div className="w-full flex justify-around items-center text-xs sm:text-sm text-gray-300 border-t border-gray-700 pt-3 mt-2">
                    <span>{formatToPersianClock(_item.date_create)}</span>
                    <span>{formatToPersianDate(_item.date_create)}</span>
                  </div>

                  {/* Action Buttons */}
                  {!_item.is_auto && (
                    <div className="w-full flex justify-center items-center gap-3 mt-2">
                      <button
                        onClick={() => handleChangeOrderType(_item.id, "L")}
                        className="flex-1 max-w-[100px] px-3 py-1.5 text-sm
                          border-2 border-red-600 text-red-600
                          hover:bg-red-600 hover:text-white
                          rounded-lg transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-gray-900"
                      >
                        لغو
                      </button>
                      <button
                        onClick={() => handleChangeOrderType(_item.id, "T")}
                        className="flex-1 max-w-[100px] px-3 py-1.5 text-sm
                          border-2 border-green-600 text-green-600
                          hover:bg-green-600 hover:text-white
                          rounded-lg transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-gray-900"
                      >
                        تأیید
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
};

export default AdminLiveTransactions;
